import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useSignIn, useSignUp } from '@clerk/react/legacy';
import ModalX from '../ModalX';
import { Mixpanel } from '../../services/Mixpanel';
import { useSessionStorage } from '../../services/useSessionStorage';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import { ArrowLeftIcon, EnvelopeIcon } from '@heroicons/react/20/solid';
import Image from '../Image';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';
import { referrerInviteLine, resolveSignInCopy } from '../../utils/signInCopy';
import { consumeSignInPrefill, getSignInPrefill } from '../../utils/signInPrefill';

// Re-exported so call sites get `source` alongside SIGN_IN_STATE from one import.
export { SIGN_IN_SOURCE } from '../../utils/signInCopy';

export const SIGN_IN_STATE = {
  ENTER_EMAIL: 'ENTER_EMAIL',
  SIGN_IN: 'SIGN_IN',
  SIGN_UP: 'SIGN_UP',
  VERIFY_CODE: 'VERIFY_CODE',
  FORGOT_PASSWORD_ENTER_EMAIL: 'FORGOT_PASSWORD_ENTER_EMAIL',
  FORGOT_PASSWORD_ENTER_CODE: 'FORGOT_PASSWORD_ENTER_CODE',
};

export default function SignInModal({
  open,
  setOpen,
  premiumSignIn = false,
  dismissible = true,
  headline = '',
  subtitle = '',
  // Which surface opened the modal, from SIGN_IN_SOURCE. Copy experiments are
  // scoped to it so a surface-specific arm can't leak onto the global header and
  // sidebar sign-in buttons. Unlabeled call sites get the default copy.
  source = '',
  // Given name of the referrer whose link brought this visitor here, passed by the
  // homepage welcome (routes/Base/ReferralWelcomePrompt) and empty everywhere else.
  referrerGivenName = '',
}) {
  const { signIn } = useSignIn();
  const { isLoaded, signUp, setActive } = useSignUp();
  const [searchParams, setSearchParams] = useSearchParams();
  // eslint-disable-next-line no-unused-vars
  const [_, setRedirectUrl] = useSessionStorage('redirect_url_complete', { url: '' });
  const [signInState, setSignInState] = useState(SIGN_IN_STATE.ENTER_EMAIL);
  const [sharerId, setSharerId] = useState('');
  const [code, setCode] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // SSO options are shown first; the email option is secondary and revealed on demand.
  const [showEmailForm, setShowEmailForm] = useState(false);
  // experiment-packet-sign-in-copy: alternate headline/subtitle, scoped to `source`.
  const { experimentPacketSignInCopy } = useFlags();
  const { isLDidentified } = useDiffitUserContext();
  // Reading the flags here rather than at the call site also keeps the exposure
  // honest: useFlags() proxies ldClient.variation(), so a read counts as a flag
  // evaluation on LaunchDarkly's side. Inside the modal that only happens when
  // the modal actually mounts, not on every packet-page render.
  const copy = resolveSignInCopy({
    headline,
    subtitle,
    source,
    isLDidentified,
    premiumSignIn,
    experimentPacketSignInCopy,
    referrerGivenName,
  });

  const signInWith = (strategy, identifier = '') => {
    // Include success=True alongside the existing params
    let existing = window.location.search;
    // Remove the leading question mark
    if (existing.charAt(0) === '?') {
      existing = existing.substring(1);
      existing = '&' + existing;
    }

    let authenticateDict = {
      strategy,
      redirectUrl: window.location.origin + '/auth',
      redirectUrlComplete:
        window.location.pathname + '?success=true' + existing + window.location.hash,
    };
    if (identifier) {
      authenticateDict.identifier = identifier;
    }

    return signIn.authenticateWithRedirect(authenticateDict);
  };

  useEffect(() => {
    setErrorMessage('');
  }, [open, signInState]);

  useEffect(() => {
    // Reset form
    if (open) {
      setErrorMessage('');
      setSignInState(SIGN_IN_STATE.ENTER_EMAIL);
      setShowEmailForm(false);
      // This instance is app-wide and never unmounts, so whatever the last person typed
      // is still in these controlled fields. Start from the share-link values instead —
      // a shared classroom laptop must not hand the next teacher the previous one's
      // address, and the email is the one field that survives a close by design.
      const prefill = getSignInPrefill();
      setEmailAddress(prefill.email);
      setSharerId(prefill.sharerId);
      setPassword('');
      setFirstName('');
      setLastName('');
      setCode('');
    }
  }, [open]);

  useEffect(() => {
    const { email, sharerId: capturedSharerId, edited } = consumeSignInPrefill(searchParams);
    if (email) setEmailAddress(email);
    if (capturedSharerId) setSharerId(capturedSharerId);
    if (edited) {
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!isLoaded || isLoading) {
      return;
    }
    Mixpanel.track('Email/password verify code', { sharerId: sharerId });

    setIsLoading(true);
    try {
      let completeSignUp;
      if (signInState === SIGN_IN_STATE.SIGN_IN) {
        completeSignUp = await signIn.attemptFirstFactor({
          strategy: 'email_code',
          code: code,
        });
      } else {
        completeSignUp = await signUp.attemptEmailAddressVerification({
          code,
        });
      }

      if (completeSignUp.status !== 'complete') {
        /*  investigate the response, to see if there was an error
         or if the user needs to complete more steps.*/
        console.log(JSON.stringify(completeSignUp, null, 2));
      }
      if (completeSignUp.status === 'complete') {
        await setActive({ session: completeSignUp.createdSessionId });
        searchParams.append('success', true);
        setSearchParams(searchParams);
        // Need a reload for the Clerk session to be active
        window.location.reload(false);
      }
    } catch (err) {
      setErrorMessage(err.errors[0].longMessage);
      console.error(JSON.stringify(err, null, 2));
    }
    setIsLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!isLoaded || isLoading) {
      return;
    }
    Mixpanel.track('Start email/password forgot password');
    setSignInState(SIGN_IN_STATE.FORGOT_PASSWORD_ENTER_EMAIL);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!isLoaded || isLoading) {
      return;
    }
    setIsLoading(true);
    setErrorMessage('');

    try {
      // Try to create a sign-in to check what auth methods are available
      const result = await signIn.create({
        identifier: emailAddress,
      });

      if (result.status === 'needs_first_factor') {
        // Check if SAML is available
        const hasSAML = result.supportedFirstFactors.find(
          (factor) => factor.strategy === 'enterprise_sso'
        );

        // Check if Google OAuth is available
        const hasGoogleOAuth = result.supportedFirstFactors.find(
          (factor) => factor.strategy === 'oauth_google'
        );

        // Check if Microsoft OAuth is available
        const hasMicrosoftOAuth = result.supportedFirstFactors.find(
          (factor) => factor.strategy === 'oauth_microsoft'
        );

        if (hasSAML) {
          // Redirect to SAML
          Mixpanel.track('Email check - redirecting to SAML', { emailAddress });
          signInWith('enterprise_sso', emailAddress);
          let existing = window.location.search;
          if (existing.charAt(0) === '?') {
            existing = existing.substring(1);
            existing = '&' + existing;
          }
          setRedirectUrl({
            url: window.location.pathname + '?success=true' + existing + window.location.hash,
          });
          return;
        } else if (hasGoogleOAuth) {
          // Redirect to Google OAuth
          Mixpanel.track('Email check - redirecting to Google OAuth', { emailAddress });
          signInWith('oauth_google');
          let existing = window.location.search;
          if (existing.charAt(0) === '?') {
            existing = existing.substring(1);
            existing = '&' + existing;
          }
          setRedirectUrl({
            url: window.location.pathname + '?success=true' + existing + window.location.hash,
          });
          return;
        } else if (hasMicrosoftOAuth) {
          // Redirect to Microsoft OAuth
          Mixpanel.track('Email check - redirecting to Microsoft OAuth', { emailAddress });
          signInWith('oauth_microsoft');
          let existing = window.location.search;
          if (existing.charAt(0) === '?') {
            existing = existing.substring(1);
            existing = '&' + existing;
          }
          setRedirectUrl({
            url: window.location.pathname + '?success=true' + existing + window.location.hash,
          });
          return;
        } else {
          // No SAML or OAuth, proceed to sign in with password
          Mixpanel.track('Email check - existing user, no SAML', { emailAddress });
          setSignInState(SIGN_IN_STATE.SIGN_IN);
        }
      }
    } catch (err) {
      // If account doesn't exist, go to sign up
      if (err?.errors[0]?.code === 'form_identifier_not_found') {
        Mixpanel.track('Email check - new user', { emailAddress });
        setSignInState(SIGN_IN_STATE.SIGN_UP);
      } else {
        setErrorMessage(err?.errors[0]?.message || 'An error occurred');
        console.error(JSON.stringify(err, null, 2));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignInOrUp = async (e) => {
    e.preventDefault();
    if (!isLoaded || isLoading) {
      return;
    }
    setIsLoading(true);
    setErrorMessage('');

    if (signInState === SIGN_IN_STATE.SIGN_IN) {
      await signUserIn();
    } else {
      await signUserUp();
    }
    setIsLoading(false);
  };

  const signUserIn = async () => {
    Mixpanel.track('Email/password sign in', { sharerId: sharerId });
    try {
      const result = await signIn.create({
        identifier: emailAddress,
      });

      if (result.status === 'needs_first_factor') {
        // Check to see if one of the supportedFirstFactors is password. Require that.
        const hasPassword = result.supportedFirstFactors.find(
          (factor) => factor.strategy === 'password'
        );
        if (hasPassword) {
          const passwordResponse = await result.attemptFirstFactor({
            strategy: 'password',
            password: password,
          });
          if (passwordResponse.status !== 'complete') {
            setErrorMessage('Incorrect password');
            return;
          } else {
            await setActive({ session: result.createdSessionId });
            searchParams.append('success', true);
            setSearchParams(searchParams);
            // Need a reload for the Clerk session to be active
            window.location.reload();
          }
        } else if (
          result.supportedFirstFactors.find((factor) => factor.strategy === 'enterprise_sso')
        ) {
          setIsLoading(true);
          signInWith('enterprise_sso', emailAddress);
          let existing = window.location.search;
          // Remove the leading question mark
          if (existing.charAt(0) === '?') {
            existing = existing.substring(1);
            existing = '&' + existing;
          }
          setRedirectUrl({
            url: window.location.pathname + '?success=true' + existing + window.location.hash,
          });
        }

        // Otherwise, they have not set a password yet. Tell them to log in via Google.
        else {
          setErrorMessage('Please reset password or sign in with Google');
          return;
        }
      } else {
        /*Investigate why the login hasn't completed */
        console.log(result);
        console.error(JSON.stringify(result, null, 2));
      }
    } catch (err) {
      if (errorMessage === "Couldn't find your account.") {
        setErrorMessage('No account found with that email address.');
      } else {
        setErrorMessage(err?.errors[0]?.message);
        console.error(JSON.stringify(err, null, 2));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const signUserUp = async () => {
    Mixpanel.track('Email/password begin sign up', { sharerId: sharerId });

    try {
      const result = await signUp.create({
        emailAddress: emailAddress,
        password: password,
        firstName: firstName,
        lastName: lastName,
      });

      if (result.missingFields.includes('enterprise_sso')) {
        setIsLoading(true);
        signInWith('enterprise_sso', emailAddress);
        let existing = window.location.search;
        // Remove the leading question mark
        if (existing.charAt(0) === '?') {
          existing = existing.substring(1);
          existing = '&' + existing;
        }
        setRedirectUrl({
          url: window.location.pathname + '?success=true' + existing + window.location.hash,
        });
      } else {
        // send the email.
        await result.prepareEmailAddressVerification({ strategy: 'email_code' });

        // change the UI to our pending section.
        setSignInState(SIGN_IN_STATE.VERIFY_CODE);
      }
    } catch (err) {
      setErrorMessage(err.errors[0].longMessage);
      console.error(JSON.stringify(err, null, 2));
    }
  };

  function handleGoogleClick() {
    Mixpanel.track('Continue with Google', { sharerId: sharerId });
    signInWith('oauth_google');
    let existing = window.location.search;
    // Remove the leading question mark
    if (existing.charAt(0) === '?') {
      existing = existing.substring(1);
      existing = '&' + existing;
    }
    setRedirectUrl({
      url: window.location.pathname + '?success=true' + existing + window.location.hash,
    });
  }

  function handleMicrosoftClick() {
    Mixpanel.track('Continue with Microsoft', { sharerId: sharerId });
    try {
      signInWith('oauth_microsoft');
    } catch (err) {
      console.log(err);
    }
    let existing = window.location.search;
    // Remove the leading question mark
    if (existing.charAt(0) === '?') {
      existing = existing.substring(1);
      existing = '&' + existing;
    }
    setRedirectUrl({
      url: window.location.pathname + '?success=true' + existing + window.location.hash,
    });
  }

  function handleClose() {
    Mixpanel.track('Close login modal');
    setOpen(false);
  }

  const forgotPasswordSubmitEmail = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: emailAddress,
      });
      setSignInState(SIGN_IN_STATE.FORGOT_PASSWORD_ENTER_CODE);
    } catch (err) {
      console.error('error', err.errors[0].longMessage);
      if (
        err.errors[0].longMessage ===
        "`reset_password_email_code` isn't allowed for `strategy` when user's password is not set."
      ) {
        setErrorMessage('Please log in with Google');
      } else {
        setErrorMessage(err.errors[0].longMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPasswordSubmitCodeAndNewPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: code,
        password: password,
      });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        searchParams.append('success', true);
        setSearchParams(searchParams);
        // Need a reload for the Clerk session to be active
        window.location.reload(false);
      }
    } catch (err) {
      console.error('error', err.errors[0].longMessage);
      setErrorMessage(err.errors[0].longMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition.Root show={open ?? false} as={Fragment}>
      <Dialog
        as='div'
        data-allow-click-when-logged-out
        className='relative z-[70]'
        onClose={dismissible ? handleClose : () => {}}
        unmount={false}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full justify-center p-4 text-center items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'>
              <Dialog.Panel className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6'>
                <div>
                  {dismissible && <ModalX setOpen={handleClose} />}
                  {[
                    SIGN_IN_STATE.FORGOT_PASSWORD_ENTER_CODE,
                    SIGN_IN_STATE.FORGOT_PASSWORD_ENTER_EMAIL,
                    SIGN_IN_STATE.VERIFY_CODE,
                    SIGN_IN_STATE.SIGN_IN,
                    SIGN_IN_STATE.SIGN_UP,
                  ].includes(signInState) && (
                    <div className='absolute left-0 top-0 pl-4 pt-4'>
                      <button
                        type='button'
                        autoFocus={false}
                        className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2'
                        onClick={() => {
                          if (signInState === SIGN_IN_STATE.VERIFY_CODE) {
                            setSignInState(SIGN_IN_STATE.SIGN_UP);
                          } else if (
                            signInState === SIGN_IN_STATE.SIGN_IN ||
                            signInState === SIGN_IN_STATE.SIGN_UP
                          ) {
                            setSignInState(SIGN_IN_STATE.ENTER_EMAIL);
                            setShowEmailForm(false);
                          } else {
                            setSignInState(SIGN_IN_STATE.ENTER_EMAIL);
                            setShowEmailForm(false);
                          }
                        }}>
                        <span autoFocus={false} className='sr-only'>
                          Back
                        </span>
                        <ArrowLeftIcon autoFocus={false} className='h-6 w-6' aria-hidden='true' />
                      </button>
                    </div>
                  )}
                  <div className='mx-auto flex h-20 w-auto items-center justify-center'>
                    <Image
                      className='h-20 w-auto'
                      src='/logo.svg'
                      aria-label='Diffit Logo'
                      alt='Diffit Logo'
                    />
                  </div>
                  {signInState === SIGN_IN_STATE.ENTER_EMAIL && (
                    <div>
                      <div className='mt-3 text-center sm:mt-5'>
                        <h3 className='text-lg leading-6 text-gray-900'>
                          <span>{copy.headline}</span>
                        </h3>
                        <div className='mt-2 text-md text-gray-500 mb-8'>
                          <span>{copy.subtitle}</span>
                        </div>
                      </div>
                      {/* Primary: Google, highlighted */}
                      <button
                        type='button'
                        onClick={handleGoogleClick}
                        className='relative w-full inline-flex items-center justify-center bg-white rounded-full px-4 py-3 text-md font-semibold text-gray-900 shadow-md hover:shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50'>
                        <Image
                          src='/google-logo.png'
                          className='absolute left-4 h-6 w-auto'
                          alt='Google Sign In'
                        />
                        <span className='font-roboto pl-6 pr-3'>Continue with Google</span>
                      </button>
                      {/* Divider */}
                      <div className='mt-6 relative'>
                        <div className='absolute inset-0 flex items-center'>
                          <div className='w-full border-t border-gray-300' />
                        </div>
                        <div className='relative flex justify-center text-sm'>
                          <span className='bg-white px-2 text-gray-500'>or</span>
                        </div>
                      </div>
                      {/* Secondary: Microsoft + email */}
                      <div className='flex flex-col items-center mt-4 gap-2'>
                        <button
                          type='button'
                          onClick={handleMicrosoftClick}
                          className='relative w-full inline-flex items-center justify-center bg-white rounded-full px-4 py-3 text-md font-semibold text-gray-900 shadow-md hover:shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50'>
                          <Image
                            src='/microsoft-logo.png'
                            className='absolute left-4 h-6 w-auto'
                            alt='Microsoft Sign In'
                          />
                          <span className='font-roboto pl-6 pr-3'>Continue with Microsoft</span>
                        </button>
                        {!showEmailForm && (
                          <button
                            type='button'
                            onClick={() => {
                              Mixpanel.track('Continue with email');
                              setErrorMessage('');
                              setShowEmailForm(true);
                            }}
                            className='relative w-full inline-flex items-center justify-center bg-white rounded-full px-4 py-3 text-md font-semibold text-gray-900 shadow-md hover:shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50'>
                            <EnvelopeIcon
                              className='absolute left-4 h-6 w-6 text-gray-500'
                              aria-hidden='true'
                            />
                            <span className='font-roboto pl-6 pr-3'>Continue with email</span>
                          </button>
                        )}
                      </div>
                      {showEmailForm && (
                        <form onSubmit={handleEmailSubmit} className='mt-2'>
                          <label
                            htmlFor='email'
                            className='block text-left w-full text-sm font-medium text-gray-700'>
                            Email address
                          </label>
                          <input
                            required
                            type='email'
                            id='email'
                            autoFocus
                            className='mt-1 w-full px-3 py-3 text-md text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                            value={emailAddress}
                            onChange={(e) => setEmailAddress(e.target.value)}
                          />
                          {errorMessage && (
                            <div className='my-2 text-sm text-center text-red-500'>
                              <span>{errorMessage}</span>
                            </div>
                          )}
                          <button
                            disabled={isLoading}
                            type='submit'
                            className='mt-4 top-0 inline-flex w-full items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-md disabled:bg-emerald-500 disabled:text-gray-50'>
                            {isLoading && (
                              <div className='w-full flex py-1 justify-center'>
                                <LoadingSpinner />
                              </div>
                            )}
                            {!isLoading && <span>Continue</span>}
                          </button>
                        </form>
                      )}
                    </div>
                  )}
                  {[SIGN_IN_STATE.SIGN_IN, SIGN_IN_STATE.SIGN_UP].includes(signInState) && (
                    <>
                      <div className='mt-3 text-center sm:mt-5'>
                        <h3 className='text-lg leading-6 text-gray-900'>
                          {signInState === SIGN_IN_STATE.SIGN_IN ? (
                            <span>Welcome back!</span>
                          ) : (
                            <span>Create your account</span>
                          )}
                        </h3>
                        <div className='mt-2 text-md text-gray-500 mb-8'>
                          <span>{emailAddress}</span>
                          {signInState === SIGN_IN_STATE.SIGN_UP && !!referrerGivenName && (
                            <span className='mt-1 block text-sm text-emerald-700'>
                              {referrerInviteLine(referrerGivenName)}
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {signInState === SIGN_IN_STATE.FORGOT_PASSWORD_ENTER_EMAIL && (
                    <div>
                      <div className='mt-3 text-center text-m font-medium text-gray-900'>
                        <span>Forgot your password? We can help.</span>
                      </div>
                      <div className='mt-2 text-center text-sm font-medium text-gray-500'>
                        <span>
                          Enter your email address below and we'll send you a code to reset your
                          password.
                        </span>
                      </div>
                      <form onSubmit={forgotPasswordSubmitEmail}>
                        <label
                          htmlFor='email'
                          className='pt-3 block text-left w-full text-sm font-medium text-gray-700'>
                          Email address
                        </label>
                        <input
                          required
                          type='email'
                          id='email'
                          className='mt-1 w-full px-3 py-3 text-md text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                          value={emailAddress}
                          onChange={(e) => setEmailAddress(e.target.value)}
                        />
                        {errorMessage && (
                          <div className='my-2 text-sm text-center text-red-500'>
                            <span>{errorMessage}</span>
                          </div>
                        )}
                        <button
                          disabled={isLoading}
                          type='submit'
                          className='mt-2 top-0 inline-flex w-full items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-md disabled:bg-emerald-500 disabled:text-gray-50'>
                          {isLoading && (
                            <div className='w-full flex py-1 justify-center'>
                              <LoadingSpinner />
                            </div>
                          )}
                          {!isLoading && <span>Send Code</span>}
                        </button>
                      </form>
                    </div>
                  )}
                  {signInState === SIGN_IN_STATE.FORGOT_PASSWORD_ENTER_CODE && (
                    <div>
                      <div className='mt-3 text-center text-m font-medium text-gray-900'>
                        <span>Your code is on the way to your inbox.</span>
                      </div>
                      <div className='mt-2 text-center text-sm font-medium text-gray-500'>
                        <span>
                          Enter the code from the email we sent you and your new password.
                        </span>
                      </div>
                      <form onSubmit={forgotPasswordSubmitCodeAndNewPassword}>
                        <label
                          htmlFor='code'
                          className='pt-3 block text-left w-full text-sm font-medium text-gray-700'>
                          Code
                        </label>
                        <input
                          required
                          type='string'
                          id='code'
                          className='mt-1 w-full px-3 py-3 text-md text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                          onChange={(e) => setCode(e.target.value)}
                        />
                        <label
                          htmlFor='password'
                          className='pt-3 block text-left w-full text-sm font-medium text-gray-700'>
                          New Password
                        </label>
                        <input
                          required
                          type='password'
                          id='password'
                          className='mt-1 w-full px-3 py-3 text-md text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        {errorMessage && (
                          <div className='my-2 text-sm text-center text-red-500'>
                            <span>{errorMessage}</span>
                          </div>
                        )}
                        <button
                          disabled={isLoading}
                          type='submit'
                          className='mt-2 top-0 inline-flex w-full items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-md disabled:bg-emerald-500 disabled:text-gray-50'>
                          {isLoading && (
                            <div className='w-full flex py-1 justify-center'>
                              <LoadingSpinner />
                            </div>
                          )}
                          {!isLoading && <span>Save New Password</span>}
                        </button>
                      </form>
                    </div>
                  )}
                  {signInState === SIGN_IN_STATE.VERIFY_CODE && (
                    <div>
                      <div className='mt-3 text-center text-sm font-medium text-gray-900'>
                        <span>Check your email for a verification code.</span>
                      </div>
                      <input
                        type='text'
                        className='mt-1 w-full px-3 py-3 text-md text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                        onChange={(e) => setCode(e.target.value)}
                      />
                      {errorMessage && (
                        <div className='my-2 text-sm text-center text-red-500'>
                          <span>{errorMessage}</span>
                        </div>
                      )}
                      <button
                        onClick={verifyCode}
                        className='mt-4 top-0 inline-flex w-full items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-md disabled:bg-emerald-500 disabled:text-gray-50'>
                        {isLoading && (
                          <div className='w-full flex py-1 justify-center'>
                            <LoadingSpinner />
                          </div>
                        )}
                        {!isLoading && <span>Submit Code</span>}
                      </button>
                    </div>
                  )}
                  {(signInState === SIGN_IN_STATE.SIGN_IN ||
                    signInState === SIGN_IN_STATE.SIGN_UP) && (
                    <>
                      <form onSubmit={handleSignInOrUp}>
                        {signInState === SIGN_IN_STATE.SIGN_UP && (
                          <div className='flex flex-row gap-2 mb-3'>
                            <div className='basis-1/2'>
                              <label
                                htmlFor='first'
                                className='block text-left w-full text-sm font-medium text-gray-700'>
                                First name
                              </label>
                              <input
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                                type='text'
                                id='first'
                                autoFocus
                                className='mt-1 w-full px-3 py-3 text-md text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                              />
                            </div>
                            <div className='basis-1/2'>
                              <label
                                htmlFor='last'
                                className='block text-left w-full text-sm font-medium text-gray-700'>
                                Last name
                              </label>
                              <input
                                onChange={(e) => setLastName(e.target.value)}
                                required
                                type='text'
                                id='last'
                                className='mt-1 w-full px-3 py-3 text-md text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                              />
                            </div>
                          </div>
                        )}
                        <label
                          htmlFor='password'
                          className='block text-left w-full text-sm font-medium text-gray-700'>
                          Password
                        </label>
                        <input
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          type='password'
                          id='password'
                          autoFocus={signInState === SIGN_IN_STATE.SIGN_IN}
                          className='mt-1 w-full px-3 py-3 text-md text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                        />
                        {signInState === SIGN_IN_STATE.SIGN_IN && (
                          <p
                            className='mt-2 text-xs text-center w-full hover:text-gray-800 hover:cursor-pointer'
                            onClick={handleForgotPassword}
                            disabled={isLoading}>
                            <span>Forgot your password?</span>
                          </p>
                        )}
                        <div className='items-center flex-col mt-2'>
                          <div className='w-full'>
                            {errorMessage && (
                              <div className='my-2 text-sm text-center text-red-500'>
                                <span>{errorMessage}</span>
                              </div>
                            )}
                            <button
                              disabled={isLoading}
                              type='submit'
                              className='top-0 inline-flex w-full items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-md disabled:bg-emerald-500 disabled:text-gray-50'>
                              {isLoading && (
                                <div className='w-full flex py-1 justify-center'>
                                  <LoadingSpinner />
                                </div>
                              )}
                              {signInState === SIGN_IN_STATE.SIGN_IN && !isLoading && (
                                <span>Sign in</span>
                              )}
                              {signInState === SIGN_IN_STATE.SIGN_UP && !isLoading && (
                                <span>Create account</span>
                              )}
                            </button>
                          </div>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
